import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import { analyzeResumeClarity, ClarityAnalysis } from "../utils/resumeParser";
import toast from "react-hot-toast";
import "../styles/ResumeAnalysis.css";

interface Resume {
  id: string;
  fileName: string;
  isPrimary: boolean;
  content?: string; // For client-side analysis
}

interface ATSBreakdown {
  contactInfo: { email: boolean; phone: boolean; linkedin: boolean };
  sections: { experience: boolean; education: boolean; skills: boolean; summary: boolean };
  quantifiableAchievements: number;
  actionVerbs: number;
  technicalKeywords: number;
  wordCount: number;
}

interface SkillGap {
  matchPercentage: number;
  matchedSkills: string[];
  missingSkills: string[];
  extraSkills: string[];
  recommendation: string;
}

interface AnalysisResult {
  atsScore: number;
  atsBreakdown: ATSBreakdown;
  skills: { technical: string[]; soft: string[]; tools: string[] };
  skillGap: SkillGap | null;
  jobMatchPercentage: number;
  improvements: string[];
  wordCount: number;
  characterCount: number;
  clarityFromLLM?: ClarityAnalysis;
  llmPowered?: boolean;
}

const ResumeAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState<string>("");
  const [jobDescription, setJobDescription] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [loadingResumes, setLoadingResumes] = useState(true);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [clarityAnalysis, setClarityAnalysis] = useState<ClarityAnalysis | null>(null);
  const [activeTab, setActiveTab] = useState<"ats" | "skills" | "comparison" | "clarity">("ats");

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      setLoadingResumes(true);
      const response = await axiosInstance.get("/auth/resumes");
      setResumes(response.data.resumes);
      // Auto-select primary resume
      const primary = response.data.resumes.find((r: Resume) => r.isPrimary);
      if (primary) setSelectedResumeId(primary.id);
    } catch (error) {
      toast.error("Failed to load resumes");
    } finally {
      setLoadingResumes(false);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedResumeId && resumes.length === 0) {
      toast.error("Please upload a resume first");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post("/auth/resume/analyze", {
        resumeId: selectedResumeId || undefined,
        jobDescription: jobDescription || undefined,
      });
      setAnalysis(response.data);

      // Prefer LLM clarity from backend; fall back to client-side heuristic
      if (response.data.clarityFromLLM) {
        setClarityAnalysis(response.data.clarityFromLLM);
      } else if (response.data.resumeText) {
        const clarity = analyzeResumeClarity(response.data.resumeText);
        setClarityAnalysis(clarity);
      }
      
      toast.success(response.data.llmPowered ? "AI analysis complete!" : "Analysis complete!");
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to analyze resume");
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "#22c55e";
    if (score >= 60) return "#f59e0b";
    if (score >= 40) return "#f97316";
    return "#ef4444";
  };

  const renderATSTab = () => {
    if (!analysis) return null;

    return (
      <div className="ra-tab-content">
        {/* ATS Score Circle */}
        <div className="ra-score-section">
          <div 
            className="ra-score-circle"
            style={{ borderColor: getScoreColor(analysis.atsScore) }}
          >
            <span className="ra-score-value">{analysis.atsScore}</span>
            <span className="ra-score-label">ATS Score</span>
          </div>
          <p className="ra-score-description">
            {analysis.atsScore >= 80 
              ? "🎉 Excellent! Your resume is ATS-optimized."
              : analysis.atsScore >= 60 
                ? "👍 Good, but there's room for improvement."
                : "⚠️ Your resume needs optimization."}
          </p>
        </div>

        {/* Breakdown */}
        <div className="ra-breakdown">
          <h3>📊 Score Breakdown</h3>
          
          <div className="ra-breakdown-section">
            <h4>Contact Information</h4>
            <div className="ra-checklist">
              <div className={`ra-check-item ${analysis.atsBreakdown.contactInfo.email ? "passed" : "failed"}`}>
                {analysis.atsBreakdown.contactInfo.email ? "✓" : "✗"} Email Address
              </div>
              <div className={`ra-check-item ${analysis.atsBreakdown.contactInfo.phone ? "passed" : "failed"}`}>
                {analysis.atsBreakdown.contactInfo.phone ? "✓" : "✗"} Phone Number
              </div>
              <div className={`ra-check-item ${analysis.atsBreakdown.contactInfo.linkedin ? "passed" : "failed"}`}>
                {analysis.atsBreakdown.contactInfo.linkedin ? "✓" : "✗"} LinkedIn Profile
              </div>
            </div>
          </div>

          <div className="ra-breakdown-section">
            <h4>Key Sections</h4>
            <div className="ra-checklist">
              <div className={`ra-check-item ${analysis.atsBreakdown.sections.experience ? "passed" : "failed"}`}>
                {analysis.atsBreakdown.sections.experience ? "✓" : "✗"} Work Experience
              </div>
              <div className={`ra-check-item ${analysis.atsBreakdown.sections.education ? "passed" : "failed"}`}>
                {analysis.atsBreakdown.sections.education ? "✓" : "✗"} Education
              </div>
              <div className={`ra-check-item ${analysis.atsBreakdown.sections.skills ? "passed" : "failed"}`}>
                {analysis.atsBreakdown.sections.skills ? "✓" : "✗"} Skills Section
              </div>
              <div className={`ra-check-item ${analysis.atsBreakdown.sections.summary ? "passed" : "failed"}`}>
                {analysis.atsBreakdown.sections.summary ? "✓" : "✗"} Summary/Objective
              </div>
            </div>
          </div>

          <div className="ra-breakdown-section">
            <h4>Content Quality</h4>
            <div className="ra-metrics">
              <div className="ra-metric">
                <span className="ra-metric-value">{analysis.atsBreakdown.quantifiableAchievements}</span>
                <span className="ra-metric-label">Quantifiable Achievements</span>
              </div>
              <div className="ra-metric">
                <span className="ra-metric-value">{analysis.atsBreakdown.actionVerbs}</span>
                <span className="ra-metric-label">Action Verbs</span>
              </div>
              <div className="ra-metric">
                <span className="ra-metric-value">{analysis.atsBreakdown.technicalKeywords}</span>
                <span className="ra-metric-label">Technical Keywords</span>
              </div>
              <div className="ra-metric">
                <span className="ra-metric-value">{analysis.atsBreakdown.wordCount}</span>
                <span className="ra-metric-label">Word Count</span>
              </div>
            </div>
          </div>
        </div>

        {/* Improvements */}
        {analysis.improvements.length > 0 && (
          <div className="ra-improvements">
            <h3>💡 Suggested Improvements</h3>
            <ul>
              {analysis.improvements.map((imp, idx) => (
                <li key={idx}>{imp}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderSkillsTab = () => {
    if (!analysis) return null;

    return (
      <div className="ra-tab-content">
        <div className="ra-skills-grid">
          <div className="ra-skills-category">
            <h3>🔧 Technical Skills ({analysis.skills.technical.length})</h3>
            <div className="ra-skills-tags">
              {analysis.skills.technical.length > 0 ? (
                analysis.skills.technical.map((skill, idx) => (
                  <span key={idx} className="ra-skill-tag technical">{skill}</span>
                ))
              ) : (
                <p className="ra-no-skills">No technical skills detected</p>
              )}
            </div>
          </div>

          <div className="ra-skills-category">
            <h3>🧠 Soft Skills ({analysis.skills.soft.length})</h3>
            <div className="ra-skills-tags">
              {analysis.skills.soft.length > 0 ? (
                analysis.skills.soft.map((skill, idx) => (
                  <span key={idx} className="ra-skill-tag soft">{skill}</span>
                ))
              ) : (
                <p className="ra-no-skills">No soft skills detected</p>
              )}
            </div>
          </div>

          <div className="ra-skills-category">
            <h3>🛠️ Tools & Software ({analysis.skills.tools.length})</h3>
            <div className="ra-skills-tags">
              {analysis.skills.tools.length > 0 ? (
                analysis.skills.tools.map((tool, idx) => (
                  <span key={idx} className="ra-skill-tag tool">{tool}</span>
                ))
              ) : (
                <p className="ra-no-skills">No tools detected</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderComparisonTab = () => {
    if (!analysis?.skillGap) {
      return (
        <div className="ra-tab-content">
          <div className="ra-comparison-prompt">
            <h3>📋 Compare Against a Job</h3>
            <p>Paste a job description above and click "Analyze Resume" to see how your skills match up.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="ra-tab-content">
        {/* Match Score */}
        <div className="ra-match-section">
          <div 
            className="ra-score-circle"
            style={{ borderColor: getScoreColor(analysis.skillGap.matchPercentage) }}
          >
            <span className="ra-score-value">{analysis.skillGap.matchPercentage}%</span>
            <span className="ra-score-label">Job Match</span>
          </div>
          <p className="ra-match-recommendation">{analysis.skillGap.recommendation}</p>
        </div>

        {/* Skills Comparison */}
        <div className="ra-comparison-grid">
          <div className="ra-comparison-section matched">
            <h3>✅ Matched Skills ({analysis.skillGap.matchedSkills.length})</h3>
            <div className="ra-skills-tags">
              {analysis.skillGap.matchedSkills.map((skill, idx) => (
                <span key={idx} className="ra-skill-tag matched">{skill}</span>
              ))}
            </div>
          </div>

          <div className="ra-comparison-section missing">
            <h3>❌ Missing Skills ({analysis.skillGap.missingSkills.length})</h3>
            <div className="ra-skills-tags">
              {analysis.skillGap.missingSkills.length > 0 ? (
                analysis.skillGap.missingSkills.map((skill, idx) => (
                  <span key={idx} className="ra-skill-tag missing">{skill}</span>
                ))
              ) : (
                <p className="ra-all-matched">Great! You have all required skills!</p>
              )}
            </div>
          </div>

          <div className="ra-comparison-section extra">
            <h3>➕ Extra Skills ({analysis.skillGap.extraSkills.length})</h3>
            <div className="ra-skills-tags">
              {analysis.skillGap.extraSkills.slice(0, 10).map((skill, idx) => (
                <span key={idx} className="ra-skill-tag extra">{skill}</span>
              ))}
              {analysis.skillGap.extraSkills.length > 10 && (
                <span className="ra-more-skills">+{analysis.skillGap.extraSkills.length - 10} more</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderClarityTab = () => {
    if (!clarityAnalysis) {
      return (
        <div className="ra-tab-content">
          <div className="ra-empty-state">
            <div className="ra-empty-icon">📋</div>
            <h3>No Clarity Analysis Yet</h3>
            <p>Run the resume analysis above to see your clarity score, missing fields, and writing improvement suggestions.</p>
          </div>
        </div>
      );
    }

    const getSeverityColor = (severity: string) => {
      switch(severity) {
        case 'critical': return '#ef4444';
        case 'high': return '#f97316';
        case 'medium': return '#f59e0b';
        default: return '#6b7280';
      }
    };

    return (
      <div className="ra-tab-content">
        {/* Clarity Score */}
        <div className="ra-score-section">
          <div 
            className="ra-score-circle"
            style={{ borderColor: getScoreColor(clarityAnalysis.overallScore) }}
          >
            <span className="ra-score-value">{clarityAnalysis.overallScore}</span>
            <span className="ra-score-label">Clarity Score</span>
          </div>
          <p className="ra-score-description">
            {clarityAnalysis.overallScore >= 80 
              ? "🎉 Excellent clarity and structure!"
              : clarityAnalysis.overallScore >= 60 
                ? "👍 Good structure, room for improvement"
                : "⚠️ Needs significant improvement for clarity"}
          </p>
        </div>

        {/* Missing Fields */}
        {clarityAnalysis.missingFields.length > 0 && (
          <div className="ra-missing-fields">
            <h3>⚠️ Missing Critical Information ({clarityAnalysis.missingFields.length})</h3>
            <div className="ra-missing-list">
              {clarityAnalysis.missingFields.map((field, idx) => (
                <div key={idx} className="ra-missing-item" style={{ borderLeftColor: getSeverityColor(field.severity) }}>
                  <div className="ra-missing-header">
                    <h4>{field.field}</h4>
                    <span className={`ra-severity ${field.severity}`}>{field.severity}</span>
                  </div>
                  <p className="ra-missing-description">{field.description}</p>
                  <p className="ra-missing-impact">💥 Impact: {field.impact}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Clarity Issues */}
        {clarityAnalysis.clarityIssues.length > 0 && (
          <div className="ra-clarity-issues">
            <h3>🔍 Clarity Issues ({clarityAnalysis.clarityIssues.length})</h3>
            <div className="ra-issues-list">
              {clarityAnalysis.clarityIssues.map((issue, idx) => (
                <div key={idx} className="ra-issue-item" style={{ borderLeftColor: getSeverityColor(issue.severity) }}>
                  <h4>{issue.description}</h4>
                  <p className="ra-issue-suggestion">💡 {issue.suggestion}</p>
                  {issue.highlights && issue.highlights.length > 0 && (
                    <div className="ra-highlights">
                      <p className="ra-highlights-label">Examples:</p>
                      {issue.highlights.map((hl, hidx) => (
                        <span key={hidx} className="ra-highlight-example" style={{ backgroundColor: '#fef3c7' }}>
                          "{hl.text}" - {hl.reason}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Improvements */}
        {clarityAnalysis.improvements.length > 0 && (
          <div className="ra-improvements">
            <h3>✨ How to Improve</h3>
            <ol className="ra-improvements-list">
              {clarityAnalysis.improvements.map((imp, idx) => (
                <li key={idx}>{imp}</li>
              ))}
            </ol>
          </div>
        )}
      </div>
    );
  };

  if (loadingResumes) {
    return (
      <div className="ra-container">
        <div className="ra-loading">⏳ Loading...</div>
      </div>
    );
  }

  return (
    <div className="ra-container">
      <div className="ra-header">
        <button onClick={() => navigate("/dashboard")} className="ra-back-btn">
          ← Back to Dashboard
        </button>
        <h1>📊 Resume Analysis</h1>
        <p>Get ATS score, skill analysis, and job matching insights</p>
      </div>

      {/* Input Section */}
      <div className="ra-input-section">
        <div className="ra-input-row">
          <div className="ra-input-group">
            <label>Select Resume</label>
            <select 
              value={selectedResumeId} 
              onChange={(e) => setSelectedResumeId(e.target.value)}
              className="ra-select"
            >
              <option value="">Use primary resume</option>
              {resumes.map((resume) => (
                <option key={resume.id} value={resume.id}>
                  {resume.fileName} {resume.isPrimary && "(Primary)"}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="ra-input-group">
          <label>Job Description (optional - for comparison)</label>
          <textarea
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder="Paste a job description here to compare your skills against the requirements..."
            className="ra-textarea"
            rows={5}
          />
        </div>

        <button 
          onClick={handleAnalyze}
          disabled={loading || (resumes.length === 0)}
          className="ra-analyze-btn"
        >
          {loading ? "🔄 Analyzing..." : "🔍 Analyze Resume"}
        </button>
      </div>

      {/* Results Section */}
      {analysis && (
        <div className="ra-results">
          {/* Tabs */}
          <div className="ra-tabs">
            <button 
              className={`ra-tab ${activeTab === "ats" ? "active" : ""}`}
              onClick={() => setActiveTab("ats")}
            >
              📈 ATS Score
            </button>
            <button 
              className={`ra-tab ${activeTab === "skills" ? "active" : ""}`}
              onClick={() => setActiveTab("skills")}
            >
              🎯 Skills
            </button>
            <button 
              className={`ra-tab ${activeTab === "comparison" ? "active" : ""}`}
              onClick={() => setActiveTab("comparison")}
            >
              ⚖️ Job Match
            </button>
            <button 
              className={`ra-tab ${activeTab === "clarity" ? "active" : ""}`}
              onClick={() => setActiveTab("clarity")}
            >
              ✨ Clarity
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "ats" && renderATSTab()}
          {activeTab === "skills" && renderSkillsTab()}
          {activeTab === "comparison" && renderComparisonTab()}
          {activeTab === "clarity" && renderClarityTab()}
        </div>
      )}

      {/* No Resume Warning */}
      {resumes.length === 0 && (
        <div className="ra-no-resume">
          <p>📄 No resume found. Please upload a resume first.</p>
          <button onClick={() => navigate("/resumes")} className="ra-upload-btn">
            Upload Resume
          </button>
        </div>
      )}
    </div>
  );
};

export default ResumeAnalysis;
